import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import { LOGO_NAME_GREEN, API } from "../../constants";
import ContactList from "./ContactList";
import CalendarTab from "./Calendar";
import CalendarIntegration from "./CalendarIntegration";
import DashboardTab from "./DashboardTab";
import AccessLog from "./AccessLog";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" },
  { id: "phonebook", label: "Phone Book", icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
  { id: "calendar", label: "Calendar", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
  { id: "cal-integration", label: "Integration", icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" },
  { id: "access-log", label: "Access Log", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
];

/**
 * CRMPage — thin shell. Owns auth + active tab.
 * Each tab is its own component file under /pages/CRM/.
 */
export default function CRMPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/verify-password`, { password });
      setIsAuthenticated(true);
    } catch {
      toast.error("Invalid password");
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4">
          <div className="card-glass p-8 w-full max-w-xs" data-testid="crm-login">
            <div className="text-center mb-8">
              <img src={LOGO_NAME_GREEN} alt="Top Screen" className="mx-auto mb-4" style={{ height: "180px" }} />
              <h1 className="font-heading text-2xl font-bold text-white">CRM Access</h1>
              <p className="text-[#E8E4DC]">Enter Password to Continue</p>
            </div>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark w-full mb-4 text-center"
                placeholder="Password"
                data-testid="crm-password"
              />
              <button type="submit" className="btn-primary w-full" data-testid="crm-submit">
                Access CRM
              </button>
            </form>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl font-bold text-white mb-6" data-testid="crm-title">
            CRM Dashboard
          </h1>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-8 flex-wrap" data-testid="crm-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? "bg-[#A3E635] text-black" : "text-[#E8E4DC] hover:text-white hover:bg-white/5"}`}
                data-testid={`crm-tab-${tab.id}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "dashboard" && <DashboardTab adminPassword={password} />}
          {activeTab === "phonebook" && <ContactList />}
          {activeTab === "calendar" && <CalendarTab />}
          {activeTab === "cal-integration" && <CalendarIntegration />}
          {activeTab === "access-log" && <AccessLog />}
        </div>
      </div>
    </Layout>
  );
}
